import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Row, Col } from "reactstrap";
import "../../../assets/css/gamesCss/Graph/insertDeleteDirected.css";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/colors.css";
import { useLanguage } from "context/LanguageProvider";



const SVG_WIDTH = 700;
const SVG_HEIGHT = 450;
const NODE_SIZE = 50;

const initialNodes = [
  { id: "A", label: "A", x: 100, y: 80 },
  { id: "B", label: "B", x: 230, y: 30 },
  { id: "C", label: "C", x: 400, y: 80 },
  { id: "D", label: "D", x: 550, y: 100 },
  { id: "E", label: "E", x: 60, y: 200 },
  { id: "F", label: "F", x: 250, y: 150 },
  { id: "G", label: "G", x: 400, y: 200 },
  { id: "H", label: "H", x: 560, y: 250 },
  { id: "I", label: "I", x: 175, y: 350 },
  { id: "J", label: "J", x: 325, y: 300 },
  { id: "K", label: "K", x: 400, y: 380 },
];


const initialStates = [
  {
    // Αποστολή 1: Κρίση δικτύου - Ο B είναι υπερφορτωμένος
    nodes: initialNodes,
    edges: [
      { from: "A", to: "B", id: "A-B-init" },  // ✓ Αυτή πρέπει να διαγραφεί
      { from: "B", to: "F", id: "B-F-init" },  // ✓ Αυτή πρέπει να διαγραφεί
      { from: "E", to: "I", id: "E-I-init" },  // ✓ Υπάρχουσα
      { from: "F", to: "J", id: "F-J-init" },  // ✓ Υπάρχουσα
      { from: "G", to: "K", id: "G-K-init" },
      { from: "C", to: "D", id: "C-D-init" },
      { from: "G", to: "B", id: "G-B-init" },
    ]
  },
  {
    // Αποστολή 2: Κυκλικό δίκτυο - Οι I,J,K υπάρχουν αλλά ΔΕΝ είναι συνδεδεμένοι κυκλικά
    nodes: initialNodes,
    edges: [
      { from: "A", to: "E", id: "A-E-init" },
      { from: "E", to: "F", id: "E-F-init" },
      { from: "E", to: "I", id: "E-I-init" },
      { from: "F", to: "J", id: "F-J-init" },
      { from: "G", to: "K", id: "G-K-init" },
      // I, J, K δεν έχουν μεταξύ τους συνδέσεις ακόμα
    ]
  },
  {
    // Αποστολή 3: Κατεδάφιση - Οι E και F υπάρχουν ακόμα αλλά θα διαγραφούν
    nodes: initialNodes,
    edges: [
      { from: "A", to: "E", id: "A-E-init" },
      { from: "E", to: "F", id: "E-F-init" },
      { from: "E", to: "I", id: "E-I-init" },
      { from: "F", to: "J", id: "F-J-init" },
      { from: "I", to: "J", id: "I-J-init" },
      { from: "J", to: "K", id: "J-K-init" },
      { from: "K", to: "I", id: "K-I-init" },
      { from: "G", to: "K", id: "G-K-init" },
    ]
  },
  {
    // Αποστολή 4: Επισκευή C - Ο C έχει εξερχόμενες ακμές που πρέπει να διαγραφούν
    nodes: initialNodes.filter(n => n.id !== "E" && n.id !== "F"), // E και F έχουν ήδη διαγραφεί
    edges: [
      { from: "A", to: "I", id: "A-I-init" },
      { from: "C", to: "G", id: "C-G-init" }, // Αυτή πρέπει να διαγραφεί
      { from: "C", to: "H", id: "C-H-init" }, // Αυτή πρέπει να διαγραφεί
      { from: "I", to: "J", id: "I-J-init" },
      { from: "J", to: "K", id: "J-K-init" },
      { from: "K", to: "I", id: "K-I-init" },
      { from: "G", to: "K", id: "G-K-init" },
    ]
  },
  {
    // Αποστολή 5: Hub στο G - Το G έχει κάποιες συνδέσεις αλλά όχι όλες
    nodes: initialNodes.filter(n => n.id !== "E" && n.id !== "F"),
    edges: [
      { from: "A", to: "I", id: "A-I-init" },
      { from: "G", to: "H", id: "G-H-init" }, // Αυτή πρέπει να διαγραφεί
      { from: "G", to: "K", id: "G-K-init" }, // Αυτή ήδη υπάρχει (οκ)
      { from: "I", to: "J", id: "I-J-init" },
      { from: "J", to: "K", id: "J-K-init" },
      { from: "K", to: "I", id: "K-I-init" },
      { from: "D", to: "G", id: "D-G-init" }, // Αυτή ήδη υπάρχει (από mission 4)
    ]
  }
];

const InsertionDeletionDirected = () => {

  const { selLanguage, toggleLanguage, isLoading } = useLanguage();
  const [nodes, setNodes] = useState([...initialStates[0].nodes]);
  const [edges, setEdges] = useState([...initialStates[0].edges]);

  const [selectedNodes, setSelectedNodes] = useState([]);
  const [missionIndex, setMissionIndex] = useState(0);
  const [message, setMessage] = useState("");
  const [lives, setLives] = useState(3);
  const [showGameOver, setShowGameOver] = useState(false);
  const [showGameWon, setShowGameWon] = useState(false);
  const [mode, setMode] = useState("select");
  const [isChecking, setIsChecking] = useState(false);
  const { darkMode } = useDarkMode();

  const missions = [
    {
      text: selLanguage
        ? "Ο κεντρικός σταθμός B έχει υπερφόρτωση! Ανακατεύθυνε ΟΛΗ την κυκλοφορία: Διέγραψε όλες τις ακμές από A→B και B→F, μετά δημιούργησε εναλλακτικές διαδρομές A→E και E→F για να παρακάμψεις τον B"
        : "Central station B is overloaded! Redirect ALL traffic: Delete all edges from A→B and B→F, then create alternative routes A→E and E→F to bypass B",
      check: (nodes, edges) =>
        !edges.some((e) => e.from === "A" && e.to === "B") &&
        !edges.some((e) => e.from === "B" && e.to === "F") &&
        edges.some((e) => e.from === "A" && e.to === "E") &&
        edges.some((e) => e.from === "E" && e.to === "F"),
    },
    {
      text: selLanguage
        ? "Οι σταθμοί I, J, K πρέπει να συνδεθούν σε ΚΥΚΛΙΚΟ δίκτυο (I→J→K→I) για καλύτερη ροή. Πρόσθεσε τις 3 ακμές που λείπουν για να κλείσει ο κύκλος"
        : "Stations I, J, K must be connected in a CIRCULAR network (I→J→K→I) for better flow. Add the 3 missing edges to close the cycle",
      check: (nodes, edges) =>
        edges.some((e) => e.from === "I" && e.to === "J") &&
        edges.some((e) => e.from === "J" && e.to === "K") &&
        edges.some((e) => e.from === "K" && e.to === "I"),
    },
    {
      text: selLanguage
        ? "Οι παλιοί σταθμοί E και F θα κλείσουν οριστικά! Πρώτα δημιούργησε εναλλακτική διαδρομή A→I, μετά ΔΙΕΓΡΑΨΕ ΠΛΗΡΩΣ τους κόμβους E και F (θα χαθούν και όλες οι συνδέσεις τους)"
        : "Old stations E and F will close permanently! First create alternative route A→I, then COMPLETELY DELETE nodes E and F (all their connections will be lost)",
      check: (nodes, edges) =>
        !nodes.some((n) => n.id === "E") &&
        !nodes.some((n) => n.id === "F") &&
        edges.some((e) => e.from === "A" && e.to === "I"),
    },
    {
      text: selLanguage
        ? "Ο σταθμός C είναι σε maintenance - ΔΕΝ μπορεί να ΣΤΕΛΝΕΙ δεδομένα (μόνο να δέχεται). Διέγραψε όλες τις ακμές που ΞΕΚΙΝΑΝΕ από C, αλλά πρόσθεσε νέα σύνδεση D→G για να αντισταθμίσεις την απώλεια"
        : "Station C is under maintenance - it CANNOT SEND data (only receive). Delete all edges STARTING from C, but add new connection D→G to compensate for the loss",
      check: (nodes, edges) =>
        !edges.some((e) => e.from === "C") &&
        edges.some((e) => e.from === "D" && e.to === "G"),
    },
    {
      text: selLanguage
        ? "Δημιούργησε HUB στον κόμβο G! Πρέπει να δέχεται από: B→G, D→G, H→G και να στέλνει προς: G→K, G→J. Διέγραψε την παλιά ακμή G→H (αντίστροφη κατεύθυνση πλέον)"
        : "Create HUB at node G! It must receive from: B→G, D→G, H→G and send to: G→K, G→J. Delete the old edge G→H (reverse direction now)",
      check: (nodes, edges) =>
        edges.some((e) => e.from === "B" && e.to === "G") &&
        edges.some((e) => e.from === "D" && e.to === "G") &&
        edges.some((e) => e.from === "H" && e.to === "G") &&
        edges.some((e) => e.from === "G" && e.to === "K") &&
        edges.some((e) => e.from === "G" && e.to === "J") &&
        !edges.some((e) => e.from === "G" && e.to === "H"),
    },
  ];
  const uniqueIdRef = useRef(`insertDelete-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    if (lives <= 0) setShowGameOver(true);
  }, [lives]);

  useEffect(() => {
    // Αφαίρεσε το if (missionIndex > 0) για να δουλεύει και στο 0
    if (missionIndex >= 0 && missionIndex < initialStates.length) {
      setNodes([...initialStates[missionIndex].nodes]);
      setEdges([...initialStates[missionIndex].edges]);
      setSelectedNodes([]);
      setMode("select");
      setMessage(""); // Καθάρισε το μήνυμα
    }
  }, [missionIndex]);

  const handleNodeClick = (nodeId) => {
    if (mode === "addEdge") {
      if (selectedNodes.includes(nodeId)) {
        setMessage(selLanguage ? "Έχεις ήδη επιλέξει αυτόν τον κόμβο!" : " You have already selected this node!");
        return;
      }

      const newSel = [...selectedNodes, nodeId];

      if (newSel.length === 2) {
        const [from, to] = newSel;

        const fromNode = nodes.find((n) => n.id === from);
        const toNode = nodes.find((n) => n.id === to);

        if (!fromNode || !toNode) {
          setMessage(selLanguage ? "Σφάλμα: Ένας από τους κόμβους δεν υπάρχει!" : " Error: One of the nodes does not exist!");
          setSelectedNodes([]);
          return;
        }

        const edgeExists = edges.some((e) => e.from === from && e.to === to);
        if (edgeExists) {
          setMessage(selLanguage ? "Η ακμή υπάρχει ήδη!" : " The edge already exists!");
          setSelectedNodes([]);
          return;
        }

        const newEdge = {
          from,
          to,
          id: `${from}-${to}-${Date.now()}-${Math.random()}`
        };

        setEdges([...edges, newEdge]);
        setMessage(selLanguage ? `Συνδέθηκε ${from} → ${to}` : ` Connected ${from} → ${to}`);
        setSelectedNodes([]);
      } else {
        setSelectedNodes(newSel);
        setMessage(selLanguage ? `Επιλέχθηκε: ${nodeId}. Διάλεξε δεύτερο κόμβο για σύνδεση.` : ` Selected: ${nodeId}. Choose second node to connect.`);
      }
    } else if (mode === "deleteNode") {
      removeNode(nodeId);
      setMode("select");
    }
  };

  const handleEdgeClick = (from, to) => {
    if (mode === "deleteEdge") {
      setEdges(edges.filter((e) => !(e.from === from && e.to === to)));
      setMessage(selLanguage ? `Η ακμή ${from} → ${to} διαγράφηκε.` : ` Edge ${from} → ${to} deleted.`);
      setMode("select");
    }
  };

  const removeNode = (nodeId) => {
    setNodes(nodes.filter((n) => n.id !== nodeId));
    setEdges(edges.filter((e) => e.from !== nodeId && e.to !== nodeId));
    setMessage(selLanguage ? `Ο κόμβος ${nodeId} διαγράφηκε.` : ` Node ${nodeId} deleted.`);
    setSelectedNodes([]);
  };

  const checkMission = async () => {
    setIsChecking(true);
    setMessage("");

    await new Promise((resolve) => setTimeout(resolve, 500));

    if (missions[missionIndex].check(nodes, edges)) {
      if (missionIndex === missions.length - 1) {
        setShowGameWon(true);
      } else {
        setMissionIndex(missionIndex + 1);
        setMessage(selLanguage ? "Σωστό! Προχώρα στην επόμενη αποστολή." : " Correct! Proceed to the next mission.");
      }
    } else {
      setMessage(selLanguage ? "Λάθος. Δοκίμασε ξανά!" : " Wrong. Try again!");
      setLives(lives - 1);
    }

    setIsChecking(false);
  };

  const resetGame = () => {
    setNodes(initialStates[0].nodes);
    setEdges(initialStates[0].edges);
    setSelectedNodes([]);
    setMissionIndex(0);
    setMessage("");
    setLives(3);
    setShowGameOver(false);
    setShowGameWon(false);
    setMode("select");
  };

  const getNodeById = (id) => nodes.find((n) => n.id === id);

  const renderEdge = (edge, idx) => {
    const from = getNodeById(edge.from);
    const to = getNodeById(edge.to);

    if (!from || !to) return null;

    const fromCenterX = from.x + NODE_SIZE / 2;
    const fromCenterY = from.y + NODE_SIZE / 2;
    const toCenterX = to.x + NODE_SIZE / 2;
    const toCenterY = to.y + NODE_SIZE / 2;

    const dx = toCenterX - fromCenterX;
    const dy = toCenterY - fromCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const unitX = dx / distance;
    const unitY = dy / distance;
    const radius = NODE_SIZE / 2;

    const startX = fromCenterX + unitX * radius;
    const startY = fromCenterY + unitY * radius;
    const endX = toCenterX - unitX * (radius + 4);
    const endY = toCenterY - unitY * (radius + 4);

    return (
      <g key={edge.id}>
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke={mode === "deleteEdge" ? "#dc2626" : "#3b82f6"}
          strokeWidth="4"
          strokeDasharray="8 6"
          markerEnd={mode === "deleteEdge" ? "url(#arrowhead-delete)" : "url(#arrowhead)"}
          filter={`url(#glow-${uniqueIdRef.current})`}
          opacity="0.95"
          style={{ cursor: mode === "deleteEdge" ? "pointer" : "default" }}
          onClick={() => handleEdgeClick(edge.from, edge.to)}
        />
        {/* Invisible wider line for easier clicking */}
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke="transparent"
          strokeWidth="16"
          style={{ cursor: mode === "deleteEdge" ? "pointer" : "default" }}
          onClick={() => handleEdgeClick(edge.from, edge.to)}
        />
      </g>
    );
  };

  return (
    <div
      className="main-content insertDeleteDirectedContainer"
      style={{
        background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
      }}
    >
      <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
      <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />

      <Col xl="12" lg="12" md="12" sm="12" xs="12">
        <div className="insertDeleteDirected-maxWidth">
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
            width: "100%"
          }}>
            <div style={{
              color: darkMode ? "white" : "#080808bb",
              padding: "12px 24px",
              borderRadius: "12px",
              fontWeight: "700",
              fontSize: "18px",
              border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #080808bb",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}>
              {selLanguage ? `Επίπεδο ${missionIndex + 1} / 5` : `Level ${missionIndex + 1} / 5`}
            </div>
            <div className="insertDeleteDirected-livesWrapper">
              <div className="insertDeleteDirected-heartsContainer">
                {[1, 2, 3].map((heartNum) => (
                  <div
                    key={heartNum}
                    className={`insertDeleteDirected-heart ${lives >= heartNum ? "active" : "inactive"}`}>
                    ❤️
                  </div>
                ))}
              </div>
            </div>
          </div>

          <br />

          <div className="insertDeleteDirected-missionBox">
            <p className="insertDeleteDirected-missionText">{missions[missionIndex].text}</p>
          </div>

          <div className="insertDeleteDirected-modeButtons">
            {[
              { mode: "addEdge", label: selLanguage ? " Προσθήκη Ακμής" : " Add Edge" },
              { mode: "deleteEdge", label: selLanguage ? " Διαγραφή Ακμής" : " Delete Edge" },
              { mode: "deleteNode", label: selLanguage ? " Διαγραφή Κόμβου" : " Delete Node" },
            ].map(({ mode: btnMode, label }) => (
              <button
                key={btnMode}
                style={{
                  background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                }}
                onClick={() => {
                  setMode(btnMode);
                  setSelectedNodes([]);
                  setMessage(
                    btnMode === "addEdge"
                      ? selLanguage ? "Επίλεξε δύο κόμβους για σύνδεση" : " Select two nodes to connect"
                      : btnMode === "deleteEdge"
                        ? selLanguage ? "Κλικ σε μια ακμή για διαγραφή" : " Click on an edge to delete"
                        : selLanguage ? "Κλικ σε έναν κόμβο για διαγραφή" : " Click on a node to delete"
                  );
                }}
                className={`insertDeleteDirected-modeButton ${mode === btnMode ? "active" : ""}`}>
                {label}
              </button>
            ))}
          </div>

          <div className={`insertDeleteDirected-frame ${isChecking ? "checking-animation" : ""}`}>
            <svg
              width="100%"
              height="auto"
              viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
              className="insertDeleteDirected-svg"
            >
              <defs>
                <marker
                  id="arrowhead"  // ΑΦΑΙΡΕΣΕ ΤΟ ${uniqueIdRef.current}
                  markerWidth="8"
                  markerHeight="6"
                  refX="7"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <polygon points="0 0, 8 3, 0 6" fill="#3b82f6" />
                </marker>
                <marker
                  id="arrowhead-delete"  // ΑΦΑΙΡΕΣΕ ΤΟ ${uniqueIdRef.current}
                  markerWidth="8"
                  markerHeight="6"
                  refX="7"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <polygon points="0 0, 8 3, 0 6" fill="#dc2626" />
                </marker>
                <filter id="glow">  // ΑΦΑΙΡΕΣΕ ΤΟ ${uniqueIdRef.current}
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <pattern
                  id={`grid-${uniqueIdRef.current}`}
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="1"
                    opacity="0.3"
                  />
                </pattern>
              </defs>

              <rect width="100%" height="100%" fill={`url(#grid-${uniqueIdRef.current})`} />

              {edges.map((edge, idx) => renderEdge(edge, idx))}

              {nodes.map((node) => {
                let fillColor = "#3b82f6";
                if (mode === "addEdge" && selectedNodes.includes(node.id)) {
                  fillColor = "#f97316";
                } else if (mode === "deleteNode") {
                  fillColor = "#dc2626";
                }

                return (
                  <g key={node.id}>
                    <motion.circle
                      cx={node.x + NODE_SIZE / 2 + 2}
                      cy={node.y + NODE_SIZE / 2 + 2}
                      r={NODE_SIZE / 2 - 2}
                      fill="rgba(0,0,0,0.2)"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                    />
                    <motion.circle
                      cx={node.x + NODE_SIZE / 2}
                      cy={node.y + NODE_SIZE / 2}
                      r={NODE_SIZE / 2 - 2}
                      fill={fillColor}
                      stroke="#1f2937"
                      strokeWidth="3"
                      style={{ cursor: "pointer" }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleNodeClick(node.id)}
                    />
                    <motion.text
                      x={node.x + NODE_SIZE / 2}
                      y={node.y + NODE_SIZE / 2 + 5}
                      textAnchor="middle"
                      fill="white"
                      fontSize="18"
                      fontWeight="700"
                      style={{ pointerEvents: "none", userSelect: "none" }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      {node.id}
                    </motion.text>
                  </g>
                );
              })}
            </svg>
          </div>

          {message && (
            <div
              className={`insertDeleteDirected-feedback ${message.includes("Σωστό")
                ? "success"
                : message.includes("Λάθος")
                  ? "error"
                  : "info"
                }`}
            >
              {message}
            </div>
          )}
          <br />

          <div className="insertDeleteDirected-controlButtons">
            <Row>
              <button
                onClick={checkMission}
                disabled={isChecking}
                className={`insertDeleteDirected-checkButton ${isChecking ? "disabled" : ""}`}
              >
                {isChecking
                  ? (selLanguage ? "Έλεγχος..." : "Checking...")
                  : (selLanguage ? "Έλεγχος Αποστολής" : "Check Mission")
                }
              </button>

              <button
                onClick={resetGame}
                className={`insertDeleteDirected-resetButton ${isChecking ? "disabled" : ""}`}
              >
                {selLanguage ? "Επανεκκίνηση" : "Reset Game"}
              </button>
            </Row>
          </div>
        </div>
      </Col>
    </div>
  );
};

export default InsertionDeletionDirected;